<template>
  <nav
    v-if="!loading && state?.status === 'ready'"
    class="product-model-variants"
    data-page-swipe-ignore
    data-navigation-kind="product"
    :aria-label="variantsTitle"
  >
    <div class="product-model-variants__title">
      {{ variantsTitle }}
    </div>
    <p
      v-if="
        state.current?.stock === false && state.availableAlternatives.length > 0
      "
      class="product-model-variants__notice"
    >
      This variant is currently out of stock. Available variants:
    </p>
    <ul class="product-model-variants__list">
      <li
        v-for="variant in state.variants"
        :key="variant.id"
        :class="[
          'product-model-variants__item',
          {
            'product-model-variants__item--current': variant.isCurrent,
            'product-model-variants__item--out-of-stock': !variant.stock,
          },
        ]"
        :data-label="`${variant.label} · ${
          variant.stock ? 'In stock' : 'Out of stock'
        }`"
      >
        <span
          v-if="variant.isCurrent"
          :class="[
            'product-model-variants__tile',
            { 'product-model-variants__tile--no-image': !variant.image },
          ]"
          aria-current="true"
          :aria-label="`${variant.label}, ${
            variant.stock ? 'In stock' : 'Out of stock'
          }`"
        >
          <img
            v-if="variant.image"
            class="product-model-variants__image"
            :src="variant.image"
            alt=""
            width="600"
            height="600"
            loading="lazy"
            decoding="async"
          />
          <span class="product-model-variants__label">{{ variant.label }}</span>
        </span>
        <router-link
          v-else
          :class="[
            'product-model-variants__tile',
            { 'product-model-variants__tile--no-image': !variant.image },
          ]"
          :to="variant.path"
          :aria-label="`${variant.label}, ${
            variant.stock ? 'In stock' : 'Out of stock'
          }`"
          draggable="false"
          @pointerenter="warmVariant(variant)"
          @focus="warmVariant(variant)"
          @pointerdown.passive="warmVariant(variant)"
          @click="prepareVariantNavigation(variant, $event)"
        >
          <img
            v-if="variant.image"
            class="product-model-variants__image"
            :src="variant.image"
            alt=""
            width="600"
            height="600"
            loading="lazy"
            decoding="async"
            draggable="false"
          />
          <span class="product-model-variants__label">{{ variant.label }}</span>
        </router-link>
        <span
          class="product-model-variants__stock-dot"
          :class="{ 'product-model-variants__stock-dot--out': !variant.stock }"
          aria-hidden="true"
        />
        <span v-if="!variant.stock" class="product-model-variants__stock"
          >Out of stock</span
        >
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { useStore } from "vuex";
import type {
  ModelVariantLink,
  ModelVariantsState,
} from "~/core/utils/modelVariants";
import { prepareProductNavigation } from "~/core/utils/productNavigation";
import { prefetchProduct } from "~/core/utils/productCache";

const props = defineProps({
  state: {
    type: Object as PropType<ModelVariantsState | null>,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const warmVariant = (variant: ModelVariantLink) => {
  if (variant.isCurrent) return Promise.resolve(null);
  return prefetchProduct(store, variant.id);
};

// Warm only the variant the shopper points to, focuses or touches. Merely
// showing this rail must not fetch four other full products during PDP startup.
// The existing in-flight cache still deduplicates intent and actual navigation.

const prepareVariantNavigation = (
  variant: ModelVariantLink,
  event: MouseEvent
) => {
  // The layout captures the image first and may prevent the router-link's
  // default navigation while its cover fades. Still prepare this exact click.
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
    return;
  prepareProductNavigation(store, {
    id: variant.id,
    name: variant.name || variant.label,
    image: variant.image.replace(/-600x600(?=\.avif(?:\?|$))/i, ""),
    images: [],
    stock: variant.stock,
    manufacturer: store.getters["store/product/get"]?.manufacturer,
    url: variant.path,
  });
};

const variantsTitle = computed(() => {
  const label = String(
    props.state?.groupLabel || props.state?.displayModel || ""
  ).trim();
  if (!label) return "Model variants";
  return /\bvariants?\b/i.test(label) ? label : `${label} variants`;
});
</script>

<style lang="scss" scoped>
.product-model-variants {
  @apply mt-5 border-y py-4;
  border-color: #e7e4df;
}

.product-model-variants__title {
  @apply text-sm font-medium text-customBlack-500;
}

.product-model-variants__notice {
  @apply mt-2 text-sm;
  color: #8a4a00;
}

.product-model-variants__list {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 4px;
}

.product-model-variants__item {
  position: relative;
  min-width: 0;
}

.product-model-variants__stock-dot {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #458368;
  pointer-events: none;
  &--out {
    background: #ba5652;
  }
}

.product-model-variants__tile {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: theme("colors.customGray.50");
  overflow: hidden;
  color: inherit;
  text-decoration: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border: 1px solid transparent;
    pointer-events: none;
    transition: border-color 120ms ease;
  }
}

a.product-model-variants__tile:hover::after {
  border-color: theme("colors.customGray.300");
}

a.product-model-variants__tile:focus-visible {
  outline: 2px solid #315ea8;
  outline-offset: 2px;
}

.product-model-variants__item--current .product-model-variants__tile::after {
  border-color: theme("colors.customBlack.500");
}

.product-model-variants__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
}

.product-model-variants__item--out-of-stock .product-model-variants__image {
  opacity: 0.45;
}

.product-model-variants__label,
.product-model-variants__stock {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.product-model-variants__tile--no-image .product-model-variants__label {
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 6px;
  clip: auto;
  clip-path: none;
  white-space: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 11px;
  line-height: 14px;
  overflow: hidden;
}

@media (max-width: 767px) {
  .product-model-variants__list {
    display: flex;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .product-model-variants__item {
    flex: 0 0 calc((100% - 16px) / 4.5);
  }

  .product-model-variants__item--current {
    order: -1;
  }
}

@media (hover: hover) and (pointer: fine) {
  .product-model-variants__item::after {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 6px);
    z-index: 20;
    width: max-content;
    max-width: 240px;
    padding: 6px 8px;
    background: #1f1f1f;
    color: #fff;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    white-space: normal;
    text-wrap: balance;
    pointer-events: none;
    transform: translate(-50%, 0);
  }

  .product-model-variants__item:hover::after,
  .product-model-variants__item:focus-within::after {
    content: attr(data-label);
    animation: product-model-variants-tooltip-in 120ms ease-out;
  }

  .product-model-variants__item--out-of-stock:hover::after,
  .product-model-variants__item--out-of-stock:focus-within::after {
    content: attr(data-label);
  }

  @keyframes product-model-variants-tooltip-in {
    from {
      opacity: 0;
      transform: translate(-50%, 4px);
    }

    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
}
</style>
